import React, { useState, useEffect } from 'react';
import { DashboardPageProps } from '../../../types/dashboard';
import AdminSidebar from '../AdminSidebar';
import MainLayout from '../../../components/layout/MainLayout';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { asList } from '../../../utils/apiList';
import axios from 'axios';
import { FaDownload, FaSpinner, FaFileExcel } from 'react-icons/fa';
import { IconType } from 'react-icons';
import * as XLSX from 'xlsx';
import '../Admin.css';


import { API_URL } from '../../../config';
interface OMRSubmission {
  id: number;
  student_id: number;
  exam_omr_template_id: number;
  image_path: string;
  processed_image_path: string;
  answers_json: string;
  score: number;
  max_score: number;
  status: string;
  error_message: string | null;
  processed_at: string;
  created_at: string;
  updated_at: string;
}

interface SubmissionResult {
  student_answers: { [key: string]: string };
  correct_answers: { [key: string]: string };
  score: number;
  max_score: number;
  total_questions: number;
  attempted: number;
  correct: number;
  score_percentage: number;
  question_details: QuestionDetail[];
  debug_image: string | null;
}

interface QuestionDetail {
  question_number: string;
  correct_option: string;
  student_option: string;
  is_correct: boolean;
}

interface User {
  id: number;
  email: string;
  username: string;
  role: string;
  full_name: string;
  created_at: string;
  updated_at: string;
  last_login: string;
}

interface ExamTemplate {
  id: number;
  exam_id: number;
  omr_template_id: number;
  answers_json: string;
  created_at: string;
  updated_at: string | null;
  omr_template: {
    name: string;
    description: string;
    num_questions: number;
    config: string | null;
    id: number;
    file_path: string;
    created_by: number;
    created_at: string;
    updated_at: string | null;
  };
}

interface Exam {
  id: number;
  title: string;
  description: string;
  max_questions: number;
  start_datetime: string;
  end_datetime: string | null;
  duration_minutes: number;
  max_marks: number;
  course_id: number;
  created_by: number;
  created_at: string;
  creator: {
    email: string;
    username: string;
    role: string;
    full_name: string;
  };
}

// Create properly typed icon components
const DownloadIcon = FaDownload as IconType;
const SpinnerIcon = FaSpinner as IconType;

// Create a wrapper component for icons with proper typing
const IconComponent = ({ icon: Icon, className }: { icon: IconType; className?: string }) => {
  const IconComponent = Icon as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

const OfflineExamReports: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const [students, setStudents] = useState<User[]>([]);
  const [examTemplates, setExamTemplates] = useState<ExamTemplate[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [submissions, setSubmissions] = useState<OMRSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [selectedExam, setSelectedExam] = useState<string>('');
  const [selectedSubmission, setSelectedSubmission] = useState<OMRSubmission | null>(null);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch students and exam templates on component mount
  useEffect(() => {
    fetchStudents();
    fetchExamTemplates();
    fetchExams();
  }, []);

  // Fetch submissions when filters change
  useEffect(() => {
    fetchSubmissions();
  }, [selectedStudent, selectedExam]);

  // Get auth token from localStorage
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Fetch students (users with role 'student')
  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/?limit=100000`, getAuthHeader());
      // Filter only students from the response
      const usersData = (response.data as any).items || response.data || [];
      const studentUsers = usersData.filter((user: User) => user.role === 'student');
      setStudents(studentUsers);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch exam templates
  const fetchExamTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/omr/omr/exam-templates`, getAuthHeader());
      setExamTemplates(asList(response.data));
    } catch (error) {
      console.error('Error fetching exam templates:', error);
    }
  };

  // Fetch exams
  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exams/exams/`, getAuthHeader());
      setExams(asList(response.data));
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  // Fetch submissions with filters
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/omr/omr/submissions`;
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Only add parameters if they have values
      if (selectedStudent) {
        params.append('student_id', selectedStudent);
      }
      if (selectedExam) {
        // Find the exam template for the selected exam
        const template = examTemplates.find(t => t.exam_id === parseInt(selectedExam));
        if (template) {
          params.append('exam_omr_template_id', template.id.toString());
        }
      }
      
      // Add parameters to URL if any exist
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axios.get(url, getAuthHeader());
      
      // Filter submissions based on selected filters
      let filteredSubmissions = response.data;
      if (selectedStudent) {
        filteredSubmissions = filteredSubmissions.filter(
          (submission: OMRSubmission) => submission.student_id === parseInt(selectedStudent)
        );
      }
      if (selectedExam) {
        const selectedTemplate = examTemplates.find(template => template.exam_id === parseInt(selectedExam));
        if (selectedTemplate) {
          filteredSubmissions = filteredSubmissions.filter(
            (submission: OMRSubmission) => submission.exam_omr_template_id === selectedTemplate.id
          );
        }
      }
      
      setSubmissions(filteredSubmissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle student filter change
  const handleStudentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStudent(e.target.value);
  };

  // Handle exam filter change
  const handleExamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedExam(e.target.value);
  };

  // View submission details
  const viewSubmissionDetails = async (submission: OMRSubmission) => {
    try {
      setSelectedSubmission(submission);
      const response = await axios.get(
        `${API_URL}/api/omr/omr/submissions/${submission.id}/result`,
        getAuthHeader()
      );
      setSubmissionResult(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching submission details:', error);
    }
  };

  // Export to Excel functionality (now async with detailed score)
  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch detailed results for all submissions
      const results = await Promise.all(submissions.map(async (submission) => {
        try {
          const response = await axios.get(
            `${API_URL}/api/omr/omr/submissions/${submission.id}/result`,
            getAuthHeader()
          );
          return response.data;
        } catch (err) {
          return null;
        }
      }));

      const data = submissions.map((submission, idx) => {
        const student = students.find(s => s.id === submission.student_id);
        const template = examTemplates.find(t => t.id === submission.exam_omr_template_id);
        const result = results[idx];
        let scoreDetails = '';
        let score = submission.score;
        let maxScore = submission.max_score;
        let percentage = ((submission.score / submission.max_score) * 100).toFixed(2) + '%';
        if (result) {
          score = result.score;
          maxScore = result.max_score;
          percentage = (result.score_percentage?.toFixed(2) || '0.00') + '%';
          scoreDetails = `${result.score}/${result.max_score} (${result.score_percentage?.toFixed(2) || 0}%) - Attempted: ${result.attempted}, Correct: ${result.correct}, Total: ${result.total_questions}`;
        } else {
          scoreDetails = 'N/A';
        }
        return {
          'Student': student?.full_name || 'Unknown',
          'Exam': template?.omr_template.name || 'Unknown',
          'Score': score,
          'Max Score': maxScore,
          'Percentage': percentage,
          'Score Details': scoreDetails,
          'Status': submission.status,
          'Submitted At': new Date(submission.created_at).toLocaleString()
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Submissions');
      XLSX.writeFile(wb, 'offline_exam_submissions.xlsx');
    } finally {
      setExporting(false);
    }
  };

  const pagination = usePagination(submissions, 50);

  return (
    <MainLayout user={user} sidebarContent={<AdminSidebar />} onLogout={onLogout}>
      <div className="p-6 font-kumbh">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="admin-title">Offline Exam Reports</h1>
          </div>
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-green-700 transition-colors shadow-md active:scale-95"
            disabled={exporting}
          >
            <IconComponent icon={DownloadIcon} />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <select
            value={selectedStudent}
            onChange={handleStudentChange}
            className="border rounded-md p-2 text-sm font-medium text-gray-600 focus:ring-2 focus:ring-gray-700 focus:border-blue-500"
          >
            <option value="">All Students</option>
            {students.map(student => (
              <option key={student.id} value={student.id}>{student.full_name}</option>
            ))}
          </select>

          <select
            value={selectedExam}
            onChange={handleExamChange}
            className="border rounded-md p-2 text-sm font-medium text-gray-600 focus:ring-2 focus:ring-gray-700 focus:border-blue-500"
          >
            <option value="">All Exams</option>
            {exams.map(exam => (
              <option key={exam.id} value={exam.id}>
                {exam.title}
              </option>
            ))}
          </select>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Exam</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-sm text-gray-600">
                    <IconComponent icon={SpinnerIcon} className="animate-spin inline-block mr-2" /> Loading...
                  </td>
                </tr>
              ) : submissions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-sm text-gray-600">No submissions found</td>
                </tr>
              ) : (
                pagination.paginatedData.map(submission => {
                  const student = students.find(s => s.id === submission.student_id);
                  const template = examTemplates.find(t => t.id === submission.exam_omr_template_id);
                  const exam = template ? exams.find(e => e.id === template.exam_id) : null;
                  
                  return (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-gray-900">
                        {student?.full_name || 'Unknown'}
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">
                        {exam?.title || 'Unknown'}
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          submission.status === 'processed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {submission.status}
                        </span>
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-600">
                        {new Date(submission.created_at).toLocaleString()}
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap">
                        <button
                          onClick={() => viewSubmissionDetails(submission)}
                          className="text-[#111827] hover:text-blue-900 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <Pagination {...pagination} onPageChange={pagination.setCurrentPage} onItemsPerPageChange={pagination.setItemsPerPage} />
        </div>

        {/* Details Modal */}
        {showModal && selectedSubmission && submissionResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Submission Details</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-600 hover:text-gray-600 text-2xl font-medium">×</button>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 text-sm">Student:</p>
                <p className="font-medium text-gray-900">{students.find(s => s.id === selectedSubmission.student_id)?.full_name || 'Unknown'}</p>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 text-sm">Score:</p>
                <p className="font-medium text-gray-900">
                  {submissionResult.score}/{submissionResult.max_score} ({submissionResult.score_percentage.toFixed(2)}%)
                </p>
              </div>

              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Question Details</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Question</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Correct Answer</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Student Answer</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {submissionResult.question_details.map((detail) => (
                        <tr key={detail.question_number} className="hover:bg-gray-50">
                          <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-gray-900">{detail.question_number}</td>
                          <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">{detail.correct_option}</td>
                          <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-900">{detail.student_option || '-'}</td>
                          <td className="px-8 py-6 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              detail.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {detail.is_correct ? 'Correct' : 'Incorrect'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default OfflineExamReports;
